import { randomId } from "@mantine/hooks";
import type { ComboboxItem } from "@mantine/core";
import { IGroupedLog, ILog } from "src/interfaces/ChecklistLogbooks";
import {
  type ICheckList,
  type ICheckListItem as IEntryCheckListItem,
} from "src/interfaces";

import {
  CheckListType,
  type ICheckListItem as ISettingsCheckListItem,
} from "./CheckLists.types";

export const mapCheckListLogbooks = (data: any): ComboboxItem[] => {
  const logbooks: ComboboxItem[] = [{ label: "All Logbooks", value: "*" }];
  if (data?.logbooks) {
    for (const log of data.logbooks) {
      if (log.parts) {
        logbooks.push({
          label: log.lbname,
          value: log.lbid,
          group: log.lbname,
        });
        logbooks.push(
          ...log.parts.map((l: any) => ({
            label: l.lbname,
            value: l.lbid,
            group: log.lbname,
          })),
        );
      } else {
        logbooks.push({ label: log.lbname, value: log.lbid });
      }
    }
  }
  return logbooks;
};

export const groupLogbooks = (logs: ILog[]) => {
  const parsed: IGroupedLog[] = [];
  logs &&
    logs.forEach(log => {
      if (!log.group) {
        parsed.push({ ...log, subSections: [] });
      } else {
        const matchingGroupIndex = parsed.findIndex(
          el => el.label === log.group,
        );
        if (matchingGroupIndex !== -1) {
          parsed[matchingGroupIndex].subSections.push(log);
        } else {
          const isCollection = log.label === log.group;
          parsed.push({
            label: log.group,
            value: isCollection ? log.value : "",
            subSections: isCollection ? [] : [log],
          });
        }
      }
    });
  return parsed;
};

export const parseChecklists = (checklists: ISettingsCheckListItem[]) =>
  checklists
    .map((c, i) => ({
      ...c,
      symbol: `checklist-${i}`,
      sort: c.sort == null ? i + 1 : c.sort,
    }))
    .sort((a, b) => a.sort - b.sort);

/**
 * Ensures each checklist item and nested multiple-input field has a stable id.
 * Preserves existing ids from the API; assigns new ones only when missing.
 */
export const ensureItemIds = (list: any[]): any[] =>
  (list ?? []).map(item => ({
    ...item,
    id: item.id ?? randomId(),
    fields: item.fields?.map((field: any) => ({
      ...field,
      id: field.id ?? randomId(),
    })),
  }));

const mapItemToEntryItem = (item: any): IEntryCheckListItem => {
  const unit = item.unit ?? "check";
  let value: boolean | string = "";
  if (unit === "check") value = false;

  return {
    ...item,
    text: item.text ?? "",
    unit,
    value,
    notAvailable: false,
    naAvailable: item.naAvailable ?? false,
    fields: item.fields?.map(mapItemToEntryItem) ?? [],
  };
};

export const mapSettingsChecklistToEntry = (
  checklist: ISettingsCheckListItem,
): ICheckList => ({
  cid: checklist.cid,
  name: checklist.name,
  description: checklist.description,
  data: {
    type: checklist.data.type,
    list:
      checklist.data.type === CheckListType.Group
        ? checklist.data.list.map((group: any) => ({
            ...group,
            items: (group.items ?? []).map(mapItemToEntryItem),
          }))
        : checklist.data.list.map(mapItemToEntryItem),
  },
  suffix: 1,
  sort: checklist.sort,
  version: Number(checklist.version) || 1,
  lbid: checklist.lbid,
  yachtid: checklist.yachtid,
  canCompleteChecklist: false,
  canCreateChecklist: false,
  canEditChecklist: false,
  currentLogbook: undefined,
  currentEntryId: undefined,
});
